<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useAiChatStore } from '@/stores/ai-chat'
import { useRouteContext } from '@/composables/useRouteContext'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import IconButton from '@/components/IconButton.vue'
import SelectSearchHeader from '@/components/SelectSearchHeader.vue'
import { Button } from '@kyoto2/components/ui/button'
import { Input } from '@kyoto2/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@kyoto2/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kyoto2/components/ui/dialog'
import { Brain, Plus, Trash2, PanelRightClose, Pencil, ChevronsUpDown, MessageSquare, Check } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@kyoto2/components/ui/tooltip'
import AiMessageList from './AiMessageList.vue'
import AiComposer from './AiComposer.vue'
import AiApprovalCard from './AiApprovalCard.vue'
import AiQuestionsCard from './AiQuestionsCard.vue'
import AiStallNotice from './AiStallNotice.vue'

const store = useAiChatStore()
const routeContext = useRouteContext()

const conversationSearch = ref('')
const switcherOpen = ref(false)
const searchHeader = ref<{ focus: () => void } | null>(null)
const showRename = ref(false)
const renameTitle = ref('')
const renameSaving = ref(false)
const conversationBusy = computed(() => store.isStreaming || store.compactionActive)
const confirmAction = ref<'delete' | 'clear' | null>(null)
const confirmOpen = computed({
  get: () => confirmAction.value !== null,
  set: (open: boolean) => {
    if (!open) confirmAction.value = null
  },
})
const confirmConfig = computed(() =>
  confirmAction.value === 'delete'
    ? {
        title: 'Delete conversation?',
        description: "This permanently deletes this conversation and all its messages. This can't be undone.",
        confirmLabel: 'Delete',
      }
    : {
        title: 'Clear chat?',
        description: "This discards the current messages and starts a new chat. This can't be undone.",
        confirmLabel: 'Clear',
      },
)
let searchDebounce: ReturnType<typeof setTimeout> | undefined

watch(conversationSearch, (q) => {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    void store.loadConversations(q.trim() || undefined)
  }, 250)
})

watch(switcherOpen, async (open) => {
  if (!open) return
  void store.loadConversations(conversationSearch.value.trim() || undefined)
  await nextTick()
  searchHeader.value?.focus()
})

watch(conversationBusy, (busy) => {
  if (busy) switcherOpen.value = false
})

onMounted(async () => {
  await store.loadConversations()
})

onUnmounted(() => {
  clearTimeout(searchDebounce)
})

async function selectConversation(id: number) {
  const selected = id === store.conversationId || await store.loadConversation(id)
  if (!selected) return
  switcherOpen.value = false
  conversationSearch.value = ''
}

async function selectNewChat() {
  const started = await store.startNewConversation()
  if (!started) return
  switcherOpen.value = false
  conversationSearch.value = ''
}

function openRename() {
  renameTitle.value = store.conversationTitle ?? ''
  showRename.value = true
}

async function saveRename() {
  if (renameSaving.value) return
  renameSaving.value = true
  try {
    if (await store.renameConversation(renameTitle.value)) showRename.value = false
  } finally {
    renameSaving.value = false
  }
}

async function onConfirmDestructive() {
  if (confirmAction.value === 'delete') await store.deleteConversation()
  else if (confirmAction.value === 'clear') await store.clearConversation()
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0 min-w-0">
    <div class="flex items-center justify-between gap-1.5 px-2 py-2 shrink-0 border-b border-border">
      <Popover v-model:open="switcherOpen">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="flex items-center gap-1.5 min-w-0 flex-1 h-7 rounded-md px-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:pointer-events-none disabled:opacity-50"
            :disabled="conversationBusy"
          >
            <Brain class="size-3.5 shrink-0" />
            <span class="truncate flex-1 text-left font-medium">{{ store.conversationTitle ?? 'New chat' }}</span>
            <ChevronsUpDown class="size-3 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          :side-offset="6"
          class="p-0 w-[var(--reka-popover-trigger-width)] min-w-[260px] overflow-hidden"
        >
          <SelectSearchHeader
            ref="searchHeader"
            v-model="conversationSearch"
            placeholder="Search chat history…"
          />
          <div class="max-h-72 overflow-y-auto p-1">
            <button
              type="button"
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-left hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
              :disabled="conversationBusy"
              @click="selectNewChat"
            >
              <Plus class="size-3.5 text-muted-foreground shrink-0" />
              <span class="font-medium">New chat</span>
            </button>

            <div v-if="store.conversations.length" class="my-1 h-px bg-border" />

            <button
              v-for="c in store.conversations"
              :key="c.id"
              type="button"
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-left hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
              :class="{ 'bg-accent/60': c.id === store.conversationId }"
              :disabled="conversationBusy"
              @click="selectConversation(c.id)"
            >
              <MessageSquare class="size-3.5 text-muted-foreground shrink-0" />
              <span class="truncate flex-1">{{ c.title || `Chat ${c.id}` }}</span>
              <Check v-if="c.id === store.conversationId" class="size-3.5 text-primary shrink-0" />
            </button>

            <div
              v-if="!store.conversations.length"
              class="px-2 py-6 text-center text-xs text-muted-foreground"
            >
              {{ conversationSearch.trim() ? 'No conversations match.' : 'No conversations yet.' }}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <TooltipProvider :delay-duration="300">
        <div class="flex items-center gap-0.5 shrink-0">
          <Tooltip v-if="store.conversationId">
            <TooltipTrigger as-child>
              <IconButton ariaLabel="Rename" :disabled="conversationBusy" @click="openRename">
                <Pencil class="size-3.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Rename</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger as-child>
              <IconButton ariaLabel="New chat" :disabled="conversationBusy" @click="selectNewChat">
                <Plus class="size-3.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">New chat</TooltipContent>
          </Tooltip>
          <Tooltip v-if="store.conversationId">
            <TooltipTrigger as-child>
              <IconButton intent="remove" ariaLabel="Delete conversation" :disabled="conversationBusy" @click="confirmAction = 'delete'">
                <Trash2 class="size-3.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Delete conversation</TooltipContent>
          </Tooltip>
          <Tooltip v-else-if="store.hasMessages">
            <TooltipTrigger as-child>
              <IconButton intent="remove" ariaLabel="Clear" :disabled="conversationBusy" @click="confirmAction = 'clear'">
                <Trash2 class="size-3.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Clear</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger as-child>
              <IconButton ariaLabel="Close" @click="store.toggleSidebar(false)">
                <PanelRightClose class="size-3.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">Close</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>

    <AiMessageList />
    <AiStallNotice />
    <AiApprovalCard :route-context="routeContext" />
    <AiQuestionsCard :route-context="routeContext" />
    <AiComposer :route-context="routeContext" :current-yaml="store.taskEditorYaml" />

    <Dialog v-model:open="showRename">
      <DialogContent class="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename conversation</DialogTitle>
        </DialogHeader>
        <Input v-model="renameTitle" placeholder="Title" :disabled="renameSaving || conversationBusy" @keyup.enter="saveRename" />
        <DialogFooter>
          <Button variant="outline" :disabled="renameSaving" @click="showRename = false">Cancel</Button>
          <Button :disabled="renameSaving || conversationBusy" @click="saveRename">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      v-model:open="confirmOpen"
      :title="confirmConfig.title"
      :description="confirmConfig.description"
      :confirm-label="confirmConfig.confirmLabel"
      destructive
      @confirm="onConfirmDestructive"
    />
  </div>
</template>
